import { useEffect, useState, useMemo } from 'react';

import { AxiosError } from 'axios';

import API from 'utils/api/api';
import { getErrorMessage } from 'utils/api/errors';
import { ScheduleInstanceResponse } from 'utils/api/modules/schedules/schedules.types';
import { LinksType } from 'utils/api/types';
import { useStore } from 'utils/hooks/store';
import SessionUser from 'stores/Session/SessionUser';

import { SupportedEntityModule, supportedEntityModules, entityPropertyMap } from './constants';

interface UseScheduleInstanceResult {
  loading: boolean;
  data: ScheduleInstanceResponse | null;
  error: string | null;
  links: LinksType[];
  filteredEntityModules: SupportedEntityModule[];
  assigneeLoading: boolean;
  handleAssigneeChange: (user: { uuid: string; name: string }) => Promise<void>;
}

export const useScheduleInstance = (
  scheduleId: string,
  itemId: string,
  onUpdate?: () => void,
  onClose?: () => void
): UseScheduleInstanceResult => {
  const { session, notification } = useStore();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ScheduleInstanceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assigneeLoading, setAssigneeLoading] = useState(false);

  const { activeEntityModules } = session.user as SessionUser;

  const filteredEntityModules = useMemo(
    () => activeEntityModules.filter((module): module is SupportedEntityModule => supportedEntityModules.has(module)),
    [activeEntityModules]
  );

  const links = useMemo((): LinksType[] => {
    if (!data) return [];

    const result: LinksType[] = [];

    filteredEntityModules.forEach((module) => {
      const propertyKey = entityPropertyMap[module];
      if (data[propertyKey]) {
        result.push({
          entity: module,
          instance: data[propertyKey]!,
        });
      }
    });

    return result;
  }, [data, filteredEntityModules]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setError(null);
      setLoading(true);
      try {
        const response = await API.schedules.loadScheduleInstance(scheduleId, itemId)();
        setData(response.data);
      } catch {
        setError('Failed to load schedule details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [itemId, scheduleId]);

  const handleAssigneeChange = async (user: { uuid: string; name: string }): Promise<void> => {
    setAssigneeLoading(true);
    try {
      await API.schedules.updateScheduleInstance(scheduleId, itemId, {
        assignedTo: { uuid: user.uuid },
      })();
      notification.enqueueSuccessSnackbar('Assignee updated successfully');
      onUpdate?.();
      onClose?.();
    } catch (e) {
      notification.enqueueErrorSnackbar(getErrorMessage(e as AxiosError, 'Failed to update assignee'));
    } finally {
      setAssigneeLoading(false);
    }
  };

  return { loading, data, error, links, filteredEntityModules, assigneeLoading, handleAssigneeChange };
};
